import React, { useEffect, useRef, useState } from 'react';
import { detectLanguage, detectTheme } from './i18n.js';

const API_ROOT = `${import.meta.env.BASE_URL}api/feedback`;
let turnstileLoader;

const COPY = {
  zh: {
    feedback: '反馈',
    title: '提交反馈',
    intro: '资料有误、缺少站点或功能不顺手，都可以在这里告诉我们。',
    category: '反馈类型',
    categories: {
      correction: '资料纠错',
      'missing-site': '补充站点',
      bug: '问题报告',
      suggestion: '功能建议',
    },
    site: '相关站点',
    noSite: '不指定站点',
    message: '反馈内容',
    messageLimit: (count) => `已输入 ${count} 字 · 最少 10 字 · 最多 2000 字`,
    messagePlaceholder: '请尽量说明具体问题；资料纠错可附上来源链接。',
    contact: '联系方式（可选）',
    contactPlaceholder: '邮箱、GitHub 或其他可联系到你的方式',
    privacy: '不会保存你的原始 IP；联系方式仅用于回复本次反馈。',
    submit: '发送反馈',
    submitting: '正在发送…',
    sentTitle: '反馈已收到',
    sentBody: '谢谢，你的反馈已经安全保存。',
    close: '关闭',
    unavailable: '反馈功能尚未在 Cloudflare 中完成配置。',
    challengePrompt: '提交前请完成 Cloudflare 人机验证',
    challengeFailed: '人机验证未通过，请重试。',
    submitFailed: '发送失败，请稍后重试。',
    adminTitle: '反馈管理',
    adminIntro: '输入管理员密码后查看已收集的反馈。',
    password: '管理员密码',
    login: '进入管理页',
    loggingIn: '正在验证…',
    loginFailed: '管理员密码不正确。',
    adminUnavailable: '反馈管理尚未配置完成。',
    loading: '正在读取反馈…',
    empty: '暂时没有反馈。',
    total: (count) => `共 ${count} 条反馈`,
    logout: '退出登录',
    back: '返回站点目录',
    previous: '上一页',
    next: '下一页',
    page: (page, pages) => `第 ${page} / ${pages} 页`,
    relatedSite: '相关站点',
    contactLabel: '联系方式',
    submittedAt: '提交时间',
    pagePath: '来源页面',
    newStatus: '新反馈',
  },
  en: {
    feedback: 'Feedback',
    title: 'Send feedback',
    intro: 'Report incorrect data, a missing site, a bug, or an idea for the directory.',
    category: 'Feedback type',
    categories: {
      correction: 'Data correction',
      'missing-site': 'Missing site',
      bug: 'Bug report',
      suggestion: 'Suggestion',
    },
    site: 'Related site',
    noSite: 'No specific site',
    message: 'Message',
    messageLimit: (count) => `${count} entered · minimum 10 · maximum 2000 characters`,
    messagePlaceholder: 'Describe the issue clearly. For corrections, include a source link when possible.',
    contact: 'Contact (optional)',
    contactPlaceholder: 'Email, GitHub, or another way to reach you',
    privacy: 'Your raw IP is not stored. Contact details are only used to reply to this feedback.',
    submit: 'Send feedback',
    submitting: 'Sending…',
    sentTitle: 'Feedback received',
    sentBody: 'Thank you. Your feedback has been stored securely.',
    close: 'Close',
    unavailable: 'Feedback has not been configured in Cloudflare yet.',
    challengePrompt: 'Complete the Cloudflare verification before submitting',
    challengeFailed: 'Human verification failed. Please try again.',
    submitFailed: 'Could not send feedback. Please try again later.',
    adminTitle: 'Feedback admin',
    adminIntro: 'Enter the administrator password to view collected feedback.',
    password: 'Administrator password',
    login: 'Open admin page',
    loggingIn: 'Verifying…',
    loginFailed: 'The administrator password was not accepted.',
    adminUnavailable: 'Feedback administration is not configured yet.',
    loading: 'Loading feedback…',
    empty: 'No feedback yet.',
    total: (count) => `${count} feedback item${count === 1 ? '' : 's'}`,
    logout: 'Sign out',
    back: 'Back to directory',
    previous: 'Previous',
    next: 'Next',
    page: (page, pages) => `Page ${page} of ${pages}`,
    relatedSite: 'Related site',
    contactLabel: 'Contact',
    submittedAt: 'Submitted',
    pagePath: 'Source page',
    newStatus: 'New',
  },
};

function loadTurnstile() {
  if (window.turnstile) return Promise.resolve(window.turnstile);
  if (turnstileLoader) return turnstileLoader;
  turnstileLoader = new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
    script.async = true;
    script.defer = true;
    script.addEventListener('load', () => {
      if (window.turnstile) resolve(window.turnstile);
      else reject(new Error('Turnstile unavailable'));
    });
    script.addEventListener('error', () => reject(new Error('Turnstile unavailable')));
    document.head.append(script);
  }).catch((error) => {
    turnstileLoader = null;
    throw error;
  });
  return turnstileLoader;
}

function TurnstileBox({ siteKey, language, resetVersion, onToken, onError }) {
  const container = useRef(null);
  const tokenHandler = useRef(onToken);
  const errorHandler = useRef(onError);
  tokenHandler.current = onToken;
  errorHandler.current = onError;

  useEffect(() => {
    if (!siteKey || !container.current) return undefined;
    tokenHandler.current('');
    let disposed = false;
    let widgetId;
    loadTurnstile()
      .then((turnstile) => {
        if (disposed) return;
        widgetId = turnstile.render(container.current, {
          sitekey: siteKey,
          action: 'feedback_submit',
          language: language === 'zh' ? 'zh-cn' : 'en',
          theme: 'auto',
          size: window.innerWidth < 380 ? 'compact' : 'normal',
          callback: (value) => tokenHandler.current(value),
          'expired-callback': () => tokenHandler.current(''),
          'error-callback': () => {
            tokenHandler.current('');
            errorHandler.current();
          },
        });
      })
      .catch(() => errorHandler.current());
    return () => {
      disposed = true;
      if (widgetId !== undefined && window.turnstile) window.turnstile.remove(widgetId);
    };
  }, [language, resetVersion, siteKey]);

  return <div className="turnstile-box" ref={container} />;
}

async function responseData(response) {
  try {
    return await response.json();
  } catch {
    return {};
  }
}

export function FeedbackDialog({ open, onClose, language, sites = [] }) {
  const copy = COPY[language];
  const dialog = useRef(null);
  const form = useRef(null);
  const [config, setConfig] = useState(null);
  const [category, setCategory] = useState('correction');
  const [siteId, setSiteId] = useState('');
  const [message, setMessage] = useState('');
  const [contact, setContact] = useState('');
  const [turnstileToken, setTurnstileToken] = useState('');
  const [turnstileReset, setTurnstileReset] = useState(0);
  const [state, setState] = useState('idle');
  const [error, setError] = useState('');
  const messageLength = message.trim().length;

  useEffect(() => {
    const node = dialog.current;
    if (!node) return;
    if (open && !node.open) node.showModal();
    if (!open && node.open) node.close();
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const controller = new AbortController();
    setState('idle');
    setError('');
    setConfig(null);
    setTurnstileToken('');
    fetch(API_ROOT, { headers: { Accept: 'application/json' }, signal: controller.signal })
      .then(responseData)
      .then(setConfig)
      .catch((reason) => { if (reason.name !== 'AbortError') setConfig({ enabled: false }); });
    return () => controller.abort();
  }, [open]);

  const submit = async (event) => {
    event.preventDefault();
    if (!config?.enabled || !turnstileToken || messageLength < 10) return;
    setState('submitting');
    setError('');
    const site = sites.find((item) => item.id === siteId);
    try {
      const response = await fetch(API_ROOT, {
        method: 'POST',
        headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
        body: JSON.stringify({
          category,
          siteId: site?.id || '',
          siteName: site?.name || '',
          message,
          contact,
          pagePath: window.location.pathname,
          locale: language,
          website: form.current?.elements.website?.value || '',
          turnstileToken,
        }),
      });
      const data = await responseData(response);
      if (!response.ok) throw new Error(data.error || 'submit_failed');
      setState('sent');
      setMessage('');
      setContact('');
    } catch (reason) {
      setState('idle');
      setError(reason.message === 'turnstile_failed' ? copy.challengeFailed : copy.submitFailed);
      setTurnstileToken('');
      setTurnstileReset((value) => value + 1);
    }
  };

  return (
    <dialog
      ref={dialog}
      className="feedback-dialog"
      aria-label={copy.title}
      onClose={onClose}
      onClick={(event) => { if (event.target === dialog.current) dialog.current.close(); }}
    >
      <div className="feedback-dialog__panel">
        <button className="feedback-dialog__close" type="button" onClick={() => dialog.current.close()} aria-label={copy.close}>×</button>
        {state === 'sent' ? (
          <div className="feedback-success">
            <span aria-hidden="true">✓</span>
            <h2>{copy.sentTitle}</h2>
            <p>{copy.sentBody}</p>
            <button type="button" onClick={() => dialog.current.close()}>{copy.close}</button>
          </div>
        ) : (
          <>
            <p className="eyebrow">PT Sites</p>
            <h2>{copy.title}</h2>
            <p className="feedback-dialog__intro">{copy.intro}</p>
            {config && !config.enabled && <p className="form-notice form-notice--error">{copy.unavailable}</p>}
            <form className="feedback-form" ref={form} onSubmit={submit}>
              <label>
                <span>{copy.category}</span>
                <select value={category} onChange={(event) => setCategory(event.target.value)}>
                  {Object.entries(copy.categories).map(([value, label]) => <option value={value} key={value}>{label}</option>)}
                </select>
              </label>
              <label>
                <span>{copy.site}</span>
                <select value={siteId} onChange={(event) => setSiteId(event.target.value)}>
                  <option value="">{copy.noSite}</option>
                  {sites.map((site) => <option value={site.id} key={site.id}>{site.name}</option>)}
                </select>
              </label>
              <label className="feedback-form__wide">
                <span>{copy.message}</span>
                <textarea value={message} onChange={(event) => setMessage(event.target.value)} minLength="10" maxLength="2000" rows="6" placeholder={copy.messagePlaceholder} required />
                <small className={messageLength < 10 ? 'feedback-form__count feedback-form__count--short' : 'feedback-form__count'} aria-live="polite">
                  {copy.messageLimit(messageLength)}
                </small>
              </label>
              <label className="feedback-form__wide">
                <span>{copy.contact}</span>
                <input value={contact} onChange={(event) => setContact(event.target.value)} maxLength="200" placeholder={copy.contactPlaceholder} />
              </label>
              <label className="feedback-honeypot" aria-hidden="true">
                Website<input name="website" tabIndex="-1" autoComplete="off" />
              </label>
              <div className="feedback-form__wide">
                {config?.enabled && config.siteKey && (
                  <div className="feedback-challenge">
                    <p>{copy.challengePrompt}</p>
                    <TurnstileBox
                      siteKey={config.siteKey}
                      language={language}
                      resetVersion={turnstileReset}
                      onToken={(value) => { setTurnstileToken(value); if (value) setError(''); }}
                      onError={() => setError(copy.challengeFailed)}
                    />
                  </div>
                )}
                {error && <p className="form-notice form-notice--error" role="alert">{error}</p>}
                <p className="feedback-form__privacy">{copy.privacy}</p>
                <button className="feedback-submit" type="submit" disabled={!config?.enabled || !turnstileToken || state === 'submitting' || messageLength < 10}>
                  {state === 'submitting' ? copy.submitting : copy.submit}
                </button>
              </div>
            </form>
          </>
        )}
      </div>
    </dialog>
  );
}

function formatTime(value, language) {
  const date = new Date(value);
  if (Number.isNaN(date.valueOf())) return value;
  return new Intl.DateTimeFormat(language === 'en' ? 'en-US' : 'zh-CN', {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(date);
}

export function FeedbackAdminPage() {
  const [language] = useState(detectLanguage);
  const [theme] = useState(detectTheme);
  const copy = COPY[language];
  const [phase, setPhase] = useState('loading');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [data, setData] = useState({ items: [], total: 0, page: 1, pageSize: 40 });

  const loadPage = async (page = 1) => {
    setPhase('loading');
    const response = await fetch(`${API_ROOT}/admin?page=${page}`, { headers: { Accept: 'application/json' } });
    const payload = await responseData(response);
    if (response.status === 401) {
      setPhase('login');
      return;
    }
    if (!response.ok) {
      setPhase('unavailable');
      return;
    }
    setData(payload);
    setPhase('ready');
  };

  useEffect(() => {
    document.documentElement.lang = language === 'en' ? 'en' : 'zh-CN';
    document.documentElement.dataset.theme = theme;
    document.title = `${copy.adminTitle} · PT Sites`;
    loadPage().catch(() => setPhase('unavailable'));
  }, []);

  const login = async (event) => {
    event.preventDefault();
    if (!password) return;
    setPhase('logging-in');
    setError('');
    try {
      const response = await fetch(`${API_ROOT}/admin`, {
        method: 'POST',
        headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
        body: JSON.stringify({ password }),
      });
      if (!response.ok) throw new Error('login_failed');
      setPassword('');
      await loadPage();
    } catch {
      setPhase('login');
      setError(copy.loginFailed);
    }
  };

  const logout = async () => {
    await fetch(`${API_ROOT}/admin`, { method: 'DELETE', headers: { Accept: 'application/json' } });
    setData({ items: [], total: 0, page: 1, pageSize: 40 });
    setPhase('login');
  };

  const pages = Math.max(1, Math.ceil(data.total / data.pageSize));
  return (
    <div className="admin-shell">
      <header className="admin-topbar">
        <a className="brand" href="/">
          <img className="brand__icon" src="/favicon.svg" alt="" width="38" height="38" />
          <span>PT SITES</span>
        </a>
        <a href="/">{copy.back}</a>
      </header>
      <main className="admin-main">
        <div className="admin-heading">
          <div><p className="eyebrow">PRIVATE</p><h1>{copy.adminTitle}</h1></div>
          {phase === 'ready' && <button type="button" onClick={logout}>{copy.logout}</button>}
        </div>

        {(phase === 'loading' || phase === 'logging-in') && <div className="admin-state"><span className="pulse" />{phase === 'logging-in' ? copy.loggingIn : copy.loading}</div>}
        {phase === 'unavailable' && <div className="admin-state admin-state--error">{copy.adminUnavailable}</div>}
        {phase === 'login' && (
          <form className="admin-login" onSubmit={login}>
            <h2>{copy.adminTitle}</h2>
            <p>{copy.adminIntro}</p>
            <label><span>{copy.password}</span><input type="password" value={password} onChange={(event) => setPassword(event.target.value)} autoComplete="current-password" required autoFocus /></label>
            {error && <p className="form-notice form-notice--error" role="alert">{error}</p>}
            <button className="feedback-submit" type="submit" disabled={!password}>{copy.login}</button>
          </form>
        )}

        {phase === 'ready' && (
          <section className="feedback-admin-list">
            <div className="feedback-admin-list__summary">
              <strong>{copy.total(data.total)}</strong>
              <span>{copy.page(data.page, pages)}</span>
            </div>
            {!data.items.length && <div className="admin-state">{copy.empty}</div>}
            {data.items.map((item) => (
              <article className="feedback-entry" key={item.id}>
                <header>
                  <span className="feedback-entry__category">{copy.categories[item.category] || item.category}</span>
                  <span className="feedback-entry__status">{copy.newStatus}</span>
                  <time dateTime={item.createdAt}>{formatTime(item.createdAt, language)}</time>
                </header>
                <p className="feedback-entry__message">{item.message}</p>
                <dl>
                  {item.siteName && <div><dt>{copy.relatedSite}</dt><dd>{item.siteName}{item.siteId ? ` (${item.siteId})` : ''}</dd></div>}
                  {item.contact && <div><dt>{copy.contactLabel}</dt><dd>{item.contact}</dd></div>}
                  <div><dt>{copy.pagePath}</dt><dd>{item.pagePath}</dd></div>
                  <div><dt>{copy.submittedAt}</dt><dd>{formatTime(item.createdAt, language)}</dd></div>
                </dl>
              </article>
            ))}
            {pages > 1 && (
              <div className="admin-pagination">
                <button type="button" disabled={data.page <= 1} onClick={() => loadPage(data.page - 1)}>{copy.previous}</button>
                <span>{copy.page(data.page, pages)}</span>
                <button type="button" disabled={data.page >= pages} onClick={() => loadPage(data.page + 1)}>{copy.next}</button>
              </div>
            )}
          </section>
        )}
      </main>
    </div>
  );
}

export function feedbackAdminPath(pathname) {
  const base = import.meta.env.BASE_URL.replace(/\/$/, '');
  return pathname.replace(/\/+$/, '') === `${base}/feedback-admin`;
}

export function feedbackLabel(language) {
  return COPY[language]?.feedback || COPY.zh.feedback;
}
